import {
	ChangeDetectionStrategy,
	Component,
	ContentChild,
	Directive,
	ElementRef,
	EventEmitter,
	HostBinding,
	Input,
	Output,
	TemplateRef,
	ViewChild,
	ViewEncapsulation
} from "@angular/core";
import { CdkDragDrop } from "@angular/cdk/drag-drop";

interface BpTableHeaderTemplateContext<TItem extends object> {
	$implicit: TItem[];
}

@Directive({
	selector: "[bpTableHeader]",
	standalone: false
})
export class BpTableHeaderTemplateDirective<TItem extends object> {
	@Input("bpTableHeader") data?: TItem[];

	static ngTemplateContextGuard<TItem extends object>(
		dir: BpTableHeaderTemplateDirective<TItem>,
		ctx: unknown
	): ctx is BpTableHeaderTemplateContext<TItem> {
		return true;
	}
}

interface BpTableRowTemplateContext<TItem extends object> {
	$implicit: TItem;
}

@Directive({
	selector: "[bpTableRow]",
	standalone: false
})
export class BpTableRowTemplateDirective<TItem extends object> {
	@Input("bpTableRow") data!: TItem[];

	static ngTemplateContextGuard<TItem extends object>(
		dir: BpTableRowTemplateDirective<TItem>,
		ctx: unknown
	): ctx is BpTableRowTemplateContext<TItem> {
		return true;
	}
}

@Directive({
	selector: "[bpTableCell]",
	standalone: false
})
export class BpTableCellTemplateDirective {
	@HostBinding("class") public classes = "bp-table-cell";
}

@Directive({
	selector: "[bpTableHeaderCell]",
	standalone: false
})
export class BpTableHeaderCellTemplateDirective {
	@HostBinding("class") public classes = "bp-table-header-cell";
}

@Directive({
	selector: "[bpTableCellLogo]",
	standalone: false
})
export class BpTableCellLogoTemplateDirective {
	@HostBinding("class") public classes = "bp-table-cell-logo";
}

@Directive({
	selector: "[bpTableCellLine]",
	standalone: false
})
export class BpTableCellLineTemplateDirective {
	@HostBinding("class") public classes = "bp-table-cell-line";
}

@Directive({
	selector: "[bpTableOverflowEllipsis]",
	standalone: false
})
export class BpTableCellOverflowEllipsisDirective {
	@HostBinding("class.bp-table-overflow-ellipsis") public classes = true;
}

@Directive({
	selector: "[bpTableMessage]",
	standalone: false
})
export class BpTableMessageTemplateDirective {
	@HostBinding("class.bp-table-message") public classes = true;
}

@Component({
	selector: "bp-table",
	templateUrl: "./bp-table.component.html",
	styleUrls: ["./bp-table.component.scss"],
	// eslint-disable-next-line @angular-eslint/use-component-view-encapsulation
	encapsulation: ViewEncapsulation.None,
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class BpTableComponent<TItem extends object> {
	@Input() public items: TItem[] = [];
	@Input() public isDraggable: boolean = false;

	// set this prop to `true` to scroll the table back to top
	@Input() public set scrollToTop(val: boolean) {
		if (val && this.table) {
			this.table.nativeElement.scrollTop = 0; // scroll table to top
			this.scrollToTopChange.emit(false); // then emit false to reset the input value
		}
	}
	@Output() public scrollToTopChange: EventEmitter<boolean> = new EventEmitter<boolean>();

	// emits when any asset is clicked
	@Output() public itemClicked: EventEmitter<TItem> = new EventEmitter<TItem>();
	// emits when we reach the bottom of the table (i.e. next page event)
	@Output() public scrolledToBottom: EventEmitter<void> = new EventEmitter<void>();
	@Output() public onItemDrop: EventEmitter<CdkDragDrop<TItem[]>> = new EventEmitter<CdkDragDrop<TItem[]>>();

	// TODO: make directives typed
	@ContentChild(BpTableHeaderTemplateDirective, { read: TemplateRef }) headers?: TemplateRef<unknown>;
	@ContentChild(BpTableRowTemplateDirective, { read: TemplateRef }) rows?: TemplateRef<unknown>;
	@ContentChild(BpTableMessageTemplateDirective, { read: TemplateRef }) message?: TemplateRef<unknown>;

	@ViewChild("table") table: ElementRef = null;

	public onScrolledToBottom(): void {
		this.scrolledToBottom.emit();
	}

	public itemDropped(item: CdkDragDrop<TItem[]>): void {
		this.onItemDrop.emit(item);
	}

	public onClick(item: TItem): void {
		this.itemClicked.emit(item);
	}
}
